// src/contexts/ActiveShopContext.js
// Tracks which shop the current user is acting as (own or shared)

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { auth, db } from '../firebase/config';
import NotificationService from '../services/NotificationService';
import {
  doc, getDoc, collection, query, where, getDocs,
  updateDoc, arrayUnion, arrayRemove, onSnapshot
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

const ActiveShopContext = createContext(null);

export const useActiveShop = () => {
  const ctx = useContext(ActiveShopContext);
  if (!ctx) {
    // Return safe defaults when used outside provider
    return {
      accessibleShops: [],
      activeShopId: auth.currentUser?.uid || null,
      activeShopData: null,
      isOwnShop: true,
      setActiveShop: () => {},
      loadingShopAccess: false,
      reloadShopAccess: () => {},
      newlyAddedShops: [],
      clearNewlyAddedShops: () => {}
    };
  }
  return ctx;
};

// Resolves any pending invites for the given user by email.
// Called on login (from AuthContext) and on mount (for already-logged-in users).
export const resolveShopInvites = async (firebaseUser) => {
  if (!firebaseUser || !firebaseUser.email || firebaseUser.isAnonymous) return;

  const userEmail = firebaseUser.email.toLowerCase().trim();

  try {
    const shopsRef = collection(db, 'shops');

    // Query with lowercased email (how we store it now). Also try original casing for legacy records.
    const [snapshotLower, snapshotOriginal] = await Promise.all([
      getDocs(query(shopsRef, where('pendingInviteEmails', 'array-contains', userEmail))),
      firebaseUser.email !== userEmail
        ? getDocs(query(shopsRef, where('pendingInviteEmails', 'array-contains', firebaseUser.email)))
        : Promise.resolve({ docs: [] })
    ]);

    // Merge, deduplicating by shopId
    const seen = new Set();
    const allDocs = [...snapshotLower.docs, ...snapshotOriginal.docs].filter(d => {
      if (seen.has(d.id)) return false;
      seen.add(d.id);
      return true;
    });

    if (allDocs.length === 0) return;
    const snapshot = { docs: allDocs };

    for (const shopDoc of snapshot.docs) {
      const shopId = shopDoc.id;
      const shopData = shopDoc.data();

      // Find the matching invite (case-insensitive)
      const invite = (shopData.pendingInvites || []).find(
        i => i.email?.toLowerCase().trim() === userEmail
      );
      if (!invite) continue;

      // Promote from pending → sharedUserMap
      const shopRef = doc(db, 'shops', shopId);
      await updateDoc(shopRef, {
        [`sharedUserMap.${firebaseUser.uid}`]: {
          email: userEmail,
          role: invite.role || 'staff',
          addedAt: invite.addedAt,
          addedBy: invite.addedBy
        },
        pendingInvites: arrayRemove(invite),
        pendingInviteEmails: arrayRemove(invite.email) // remove whatever casing was stored
      });

      // Add to the user's shopAccess reverse-lookup (works whether doc exists or not)
      const userRef = doc(db, 'users', firebaseUser.uid);
      await updateDoc(userRef, {
        shopAccess: arrayUnion({ shopId, shopName: shopData.name || 'Shop' })
      }).catch(() => {
        // User doc might not exist yet — non-critical, they'll get access via sharedUserMap directly
        console.warn(`Could not update shopAccess for ${firebaseUser.uid}`);
      });

      console.log(`✅ Resolved shop invite: ${shopId} for ${firebaseUser.email}`);

      // Store the newly added shop name so the shop page can surface a banner
      const added = JSON.parse(sessionStorage.getItem('newlyAddedShops') || '[]');
      added.push({ shopId, shopName: shopData.name || 'Shop' });
      sessionStorage.setItem('newlyAddedShops', JSON.stringify(added));

      // Notify the newly-added user
      try {
        await NotificationService.createNotification(firebaseUser.uid, {
          type: 'shop_access',
          subType: 'access_granted',
          title: `You've been added to ${shopData.name || 'a shop'}`,
          message: `You can now manage orders for ${shopData.name || 'this shop'} from your shop tab.`,
          actionUrl: `/shop/${shopId}`,
          priority: 'high'
        });
      } catch (_) { /* non-critical */ }
    }
  } catch (err) {
    console.error('⚠️ resolveShopInvites failed:', err);
  }
};

export const ActiveShopProvider = ({ children }) => {
  const [currentUserId, setCurrentUserId] = useState(null);
  const [currentUserEmail, setCurrentUserEmail] = useState(null);
  const [accessibleShops, setAccessibleShops] = useState([]);
  const [activeShopId, setActiveShopIdState] = useState(null);
  const activeShopIdRef = useRef(null); // always reflects latest activeShopId for async closures
  const [activeShopData, setActiveShopData] = useState(null);
  const [loadingShopAccess, setLoadingShopAccess] = useState(true);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [newlyAddedShops, setNewlyAddedShops] = useState([]);

  // Track auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && !user.isAnonymous) {
        setCurrentUserId(user.uid);
        setCurrentUserEmail(user.email);
      } else {
        setCurrentUserId(null);
        setCurrentUserEmail(null);
        setAccessibleShops([]);
        activeShopIdRef.current = null;
        setActiveShopIdState(null);
        setActiveShopData(null);
        setLoadingShopAccess(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load accessible shops when the user ID is known (or reloadTrigger changes)
  useEffect(() => {
    if (!currentUserId) return;

    let cancelled = false;

    const loadShops = async () => {
      setLoadingShopAccess(true);
      try {
        // First, resolve any pending invites (handles already-logged-in case)
        const firebaseUser = auth.currentUser;
        if (firebaseUser) {
          await resolveShopInvites(firebaseUser);
          // Pick up any shops that were just resolved from invites
          const added = JSON.parse(sessionStorage.getItem('newlyAddedShops') || '[]');
          if (added.length > 0) {
            setNewlyAddedShops(added);
            sessionStorage.removeItem('newlyAddedShops');
          }
        }

        const shops = [];

        // 1. Own shop (always first)
        const ownShopRef = doc(db, 'shops', currentUserId);
        const ownShopSnap = await getDoc(ownShopRef);
        if (ownShopSnap.exists()) {
          const d = ownShopSnap.data();
          shops.push({
            shopId: currentUserId,
            shopName: d.name || 'My Shop',
            shopImage: typeof d.profile === 'string' ? d.profile : d.profile?.image || null,
            role: 'admin',
            isOwn: true
          });
        }

        // 2. Additional owned shops + shared shops from user document
        const userRef = doc(db, 'users', currentUserId);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          const userData = userSnap.data();

          // 2a. Additional owned shops (sub-shops created by the same owner)
          const ownedShops = userData.ownedShops || [];
          for (const owned of ownedShops) {
            const ownedRef = doc(db, 'shops', owned.shopId);
            const ownedSnap = await getDoc(ownedRef);
            if (ownedSnap.exists()) {
              const d = ownedSnap.data();
              if (d.storeStatus !== 'pending_payment') {
                shops.push({
                  shopId: owned.shopId,
                  shopName: d.name || owned.shopName || 'My Shop',
                  shopImage: typeof d.profile === 'string' ? d.profile : d.profile?.image || null,
                  role: 'admin',
                  isOwn: true
                });
              }
            }
          }

          // 2b. Shared shops (shops from other users who granted access)
          const shopAccess = userData.shopAccess || [];

          for (const access of shopAccess) {
            if (access.shopId === currentUserId) continue; // skip own primary shop

            const sharedRef = doc(db, 'shops', access.shopId);
            const sharedSnap = await getDoc(sharedRef);

            if (sharedSnap.exists()) {
              const sd = sharedSnap.data();
              // Verify access is still valid
              const entry = sd.sharedUserMap?.[currentUserId];
              if (entry) {
                shops.push({
                  shopId: access.shopId,
                  shopName: sd.name || access.shopName || 'Shared Shop',
                  shopImage: typeof sd.profile === 'string' ? sd.profile : sd.profile?.image || null,
                  role: entry.role,
                  isOwn: false
                });
              }
            }
          }
        }

        if (cancelled) return;
        setAccessibleShops(shops);

        // Preserve the currently selected shop if it's still accessible.
        // Fall back to sessionStorage, then to the first shop.
        const currentId = activeShopIdRef.current;
        if (currentId && shops.some(s => s.shopId === currentId)) {
          // Already on a valid shop — keep it, refresh sessionStorage
          sessionStorage.setItem('activeShopId', currentId);
        } else {
          const savedId = sessionStorage.getItem('activeShopId');
          if (savedId && shops.some(s => s.shopId === savedId)) {
            setActiveShopIdState(savedId);
            activeShopIdRef.current = savedId;
          } else {
            const fallback = shops[0]?.shopId || currentUserId;
            setActiveShopIdState(fallback);
            activeShopIdRef.current = fallback;
            sessionStorage.removeItem('activeShopId');
          }
        }
      } catch (err) {
        console.error('❌ Error loading shop access:', err);
        if (!cancelled) {
          setAccessibleShops([]);
          setActiveShopIdState(currentUserId);
        }
      } finally {
        if (!cancelled) setLoadingShopAccess(false);
      }
    };

    loadShops();
    return () => { cancelled = true; };
  }, [currentUserId, reloadTrigger]);

  // Subscribe to the active shared shop's data in real-time
  useEffect(() => {
    if (!activeShopId || !currentUserId || activeShopId === currentUserId) {
      setActiveShopData(null);
      return;
    }

    const shopRef = doc(db, 'shops', activeShopId);
    const unsubscribe = onSnapshot(shopRef, (snap) => {
      if (snap.exists()) {
        setActiveShopData({ id: snap.id, ...snap.data() });
      } else {
        setActiveShopData(null);
      }
    });

    return () => unsubscribe();
  }, [activeShopId, currentUserId]);

  const setActiveShop = useCallback((shopId) => {
    activeShopIdRef.current = shopId;
    setActiveShopIdState(shopId);
    sessionStorage.setItem('activeShopId', shopId);
  }, []);

  const reloadShopAccess = useCallback(() => {
    setReloadTrigger(n => n + 1);
  }, []);

  const clearNewlyAddedShops = useCallback(() => {
    setNewlyAddedShops([]);
  }, []);

  const resolvedActiveShopId = activeShopId || currentUserId;
  const isOwnShop = !resolvedActiveShopId || resolvedActiveShopId === currentUserId;

  const value = {
    accessibleShops,
    activeShopId: resolvedActiveShopId,
    activeShopData,
    isOwnShop,
    setActiveShop,
    loadingShopAccess,
    reloadShopAccess,
    newlyAddedShops,
    clearNewlyAddedShops
  };

  return (
    <ActiveShopContext.Provider value={value}>
      {children}
    </ActiveShopContext.Provider>
  );
};
